import {
  CompanyId,
  GroupId,
  ProjectId,
  UserId,
} from "@custom-types/base-types";
import { SdbTeamMemberBase } from "@custom-types/teams-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** All the possible types for a member */
export type MemberTypes =
  /** Company member type when requested from company level (all members of a company) */
  | FaroCoreApiTypes.CompanyMemberBase

  /** Company member type when requested from member level (member details by providing memberId) */
  | FaroCoreApiTypes.CompanyMemberDetails

  /** Project member type when requested from project level (project details by providing projectId) */
  | FaroCoreApiTypes.ProjectMemberBase

  /** Group member type  */
  | FaroCoreApiTypes.GroupMember

  /** Types for team member */
  | SdbTeamMemberBase;

/** All the possible types for the role of a member in company or project */
export type MemberRoleTypes =
  | FaroCoreApiTypes.CompanyRole
  | FaroCoreApiTypes.ProjectRole;

/** The entity type that caused a partial role change */
export type RoleChangeSubject = "group" | "team";

/** Roles to display in readable text */
/* eslint-disable  @typescript-eslint/naming-convention -- The namings are coming from backend */
export enum RoleDisplayNames {
  COMPANY_EXECUTIVE = "Enterprise Admin",
  COMPANY_VIEWER = "Enterprise Viewer",
  COMPANY_MANAGER = "Group Manager",
  GROUP_VIEWER = "Group Viewer",
  PROJECT_MANAGER = "Project Manager",
  MEMBER = "Member",
  RESOURCE_OWNER = "Owner",
  RESOURCE_ADMIN = "Project Admin",
  RESOURCE_EDITOR = "Project Editor",
  RESOURCE_VIEWER = "Project Viewer",
}
/* eslint-enable */

/** Assignable company roles for members when updating the role */
export const AssignableUpdateCompanyMemberRole: FaroCoreApiTypes.UpdateCompanyMemberRole[] =
  ["COMPANY_EXECUTIVE", "COMPANY_VIEWER", "GROUP_VIEWER", "MEMBER"];

/** Status of a member */
export enum EMemberStatus {
  /** When user is only invited by email but not created an account yet */
  pending = "Pending",

  /** When user has created an account */
  active = "Confirmed",
}

// TODO: ST-646 Should be removed at the end of ST-646 */
/** A workaround to make it easy to extend the usage of SdbMember */
export type SdbMemberTypes = MemberTypes | SdbMember;

// TODO: ST-646 Should be removed at the end of ST-646 */
/** A workaround to make it easy to extend the usage of SdbMember */
export type PartialSdbMemberTypes = Partial<SdbMemberTypes>;

/** Combination of member and team */
export type BaseMemberAndTeam = SdbMemberTypes | FaroCoreApiTypes.Team;

/** Base props for member */
export interface BaseMemberAndTeamProps {
  /** The type of member in sphere dashboard */
  member: BaseMemberAndTeam;
}

/** Base props for member */
export interface BaseMemberProps {
  /** The type of member in sphere dashboard */
  member: SdbMemberTypes;
}

/** Base props for a user covering isLoggedInUser. In here user is defined as the person working on our app */
export interface BaseUserProps {
  /** The type of member in sphere dashboard */
  user: FaroCoreApiTypes.LoggedInUser;
}

export interface BaseMembersProps {
  /** The type of members in sphere dashboard */
  members: SdbMemberTypes[];
}

// TODO: ST-646 Do we really need this as it is only used in one place
export interface BaseMembersMapProps {
  /** The type of members map to be passed as a props in sphere dashboard */
  membersMap: Map<UserId, SdbMemberTypes>;
}

/** A type that holds the identity and the role of a member or team */
export interface MemberIdentityRoleProps {
  memberIdentityRole: Pick<SdbMemberTypes, "identity" | "role">;
}

/**
 * Ordering represents organization hierarchy
 * Please don't change the order of the roles
 */
/* eslint-disable @typescript-eslint/naming-convention */
export enum UserRoleEnum {
  MEMBER,
  RESOURCE_VIEWER,
  RESOURCE_EDITOR,
  RESOURCE_ADMIN,
  RESOURCE_OWNER,
  GROUP_VIEWER,
  COMPANY_VIEWER,
  PROJECT_MANAGER,
  COMPANY_MANAGER,
  COMPANY_EXECUTIVE,
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * This type covers all member types returned from different routes
 * The purpose of it is to remove the confusion between all these types
 * It is currently covering the following types:
 * ICompanyMemberBase, CompanyMemberDetails, IProjectMemberBase, IGroupMember, IUserAsManager & SdbTeamMemberBase
 * But it is not covering UserDetails yet
 */
export type SdbMember = {
  // TODO: ST-646 Replace undefined with null, because we want to differentiate from what backend returns.
  // Currently, I am not using null as it will break many codes that are using MemberTypes
  /** The id of the user. Only available when user is registered (not pending), otherwise null */
  id?: UserId;

  /** The email of the user. */
  email: string;

  /**
   * For pending users it it the email
   * For registered users it is the id
   */
  identity: UserId;

  /** The name of the user */
  name: string | null;

  /** The firstName of the user */
  firstName: string | null;

  /** The lastName of the user */
  lastName: string | null;

  // TODO: ST-646 Replace undefined with null, because we want to differentiate from what backend returns.
  // Currently, I am not using null as it will break many codes that are using MemberTypes
  /** The thumbnail of the user */
  thumbnailUrl?: string;

  /**
   * Specifies the type of the entity:
   * "user": Represents an individual user.
   * "user-group": Represents a team.
   */
  kind: FaroCoreApiTypes.Kind;

  /** The permissions that the user has in company */
  permissions: FaroCoreApiTypes.UserPermission[] | null;

  // TODO: ST-646 This is a workaround for now to be closer to MemberTypes
  /** The highest role of member in project or company */
  role: MemberRoleTypes;

  /** The role the user has in company */
  companyProps: {
    [companyId: CompanyId]: {
      companyId: CompanyId;
      /** The highest role of the member in company or user-group */
      role: FaroCoreApiTypes.CompanyRole;

      /** The role directly assigned to the user in the company */
      userRole: FaroCoreApiTypes.CompanyRole | null;
    };
  } | null;

  /** The roles the project has in projects with the ID of the project */
  projectProps: {
    [projectId: ProjectId]: {
      projectId: ProjectId;
      role: FaroCoreApiTypes.ProjectRole;
    };
  } | null;

  /** The date the user joined a group with groupId */
  groupProps: {
    [groupId: GroupId]: {
      groupId: GroupId;
      dateJoined: FaroCoreApiTypes.UnixTimestamp;
    };
  } | null;
};

/** Sortable column fields for the members table */
export const MEMBERS_SORT_BY = [
  "user_ascending",
  "user_descending",
  "name_ascending",
  "name_descending",
  "email_ascending",
  "email_descending",
  "status_ascending",
  "status_descending",
  "role_ascending",
  "role_descending",
  "joinedDate_ascending",
  "joinedDate_descending",
] as const;

/** All possible members sort by values */
export type MembersSortBy = (typeof MEMBERS_SORT_BY)[number];
